/**
 * The zoned reader (Almanac S3, Decision 6): a plan's wall-clock `date` and
 * `time` in the zone it is handed, as an instant.
 *
 * Contract: the instant the server's
 * `datetime.combine(date, time, tzinfo=ZoneInfo(zone))` names
 * (`backend/planner/next_trip.py:404`, `fold=0`). A wall time the zone shows
 * twice is its first occurrence; a wall time the zone skips takes the offset in
 * force before the transition. One rule covers both: subtract the offset in
 * force immediately before the transition.
 *
 * The zone is an argument and nothing else: this module never reads the device
 * zone, so the answer is the same on any device. `docs/plans/
 * almanac-tag-vectors.json` binds it to CPython's answers, and the backend
 * parity test binds the server to the same file. Mirrored at
 * `mobile/src/lib/zonedInstant.ts`.
 */

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(zone: string): Intl.DateTimeFormat {
  let f = formatters.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    formatters.set(zone, f);
  }
  return f;
}

/**
 * The zone's UTC offset at instant `t`, in milliseconds: the wall clock the
 * zone shows at `t`, read back through `Date.UTC`, minus `t` (to the second).
 */
function offsetAt(zone: string, t: number): number {
  const parts: Record<string, string> = {};
  formatterFor(zone)
    .formatToParts(new Date(t))
    .forEach(({ type, value }) => {
      parts[type] = value;
    });
  const hour = h23Hour(parts);
  if (hour === null) return NaN;
  const wall = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    hour,
    Number(parts.minute),
    Number(parts.second),
  );
  return wall - Math.floor(t / 1000) * 1000;
}

/**
 * The hour of a `formatToParts` read on a 24-hour clock, or `null` when the
 * engine did not honour `hourCycle: 'h23'` (s3-i1 nb3: Hermes is unverified).
 * A `dayPeriod` part means a 12-hour clock, whose `7` may be 19:00, so the read
 * fails closed rather than naming a wrong instant; so does an hour that is not
 * a whole number from 0 to 24. Some engines render midnight as 24 even under
 * h23, so 24 reads as 0.
 */
export function h23Hour(parts: Record<string, string>): number | null {
  if (parts.dayPeriod !== undefined) return null;
  const hour = Number(parts.hour);
  if (!Number.isInteger(hour) || hour < 0 || hour > 24) return null;
  return hour % 24;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_RE = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * The instant `date` (`YYYY-MM-DD`) at `time` (`HH:MM` or `HH:MM:SS`, seconds
 * honoured) names in `zone` (an IANA name), or `null` when any input is
 * unreadable or the zone is unknown; never a throw.
 *
 * With `u` the wall time read as if it were UTC, the offsets a day either side,
 * and `oMax` the larger: the result is `u − offsetAt(u − oMax)`. With no
 * transition within a day the offsets agree and this is `u − offset`; with one,
 * `u − oMax` is the earliest candidate instant, which lies before the
 * transition exactly for the wall times `fold=0` gives the pre-transition
 * offset (the repeated hour, the skipped hour and everything earlier).
 */
export function zonedInstant(date: string, time: string, zone: string): Date | null {
  const d = DATE_RE.exec(date);
  const t = TIME_RE.exec(time);
  if (!d || !t || !zone) return null;
  const [hh, mm, ss] = [Number(t[1]), Number(t[2]), t[3] === undefined ? 0 : Number(t[3])];
  if (hh > 23 || mm > 59 || ss > 59) return null;
  const u = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), hh, mm, ss);
  // Date.UTC rolls an impossible date (2026-02-30) forward; the server refuses it.
  if (Number.isNaN(u) || new Date(u).getUTCDate() !== Number(d[3])
    || new Date(u).getUTCMonth() !== Number(d[2]) - 1) return null;
  try {
    const oMax = Math.max(offsetAt(zone, u - DAY_MS), offsetAt(zone, u + DAY_MS));
    // A read that failed closed (`h23Hour`) is NaN, and stays NaN to the end.
    if (Number.isNaN(oMax)) return null;
    const instant = u - offsetAt(zone, u - oMax);
    return Number.isNaN(instant) ? null : new Date(instant);
  } catch {
    // An unknown zone is a RangeError from Intl.
    return null;
  }
}
